import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { DIMENSIONALITY_VALUES } from 'constants/businessTool';
import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';
import { debounce } from 'lodash';
import { useGetLiabilitiesCalculation } from 'services/calculations/cashFlow';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import ToastService from 'helpers/ToastService';

import { IFormContentTypes, observedFields } from '../../utils/form';

export const useGetCashFlowLiabilitiesCalculation = () => {
  const { id: businessPlanId = '' } = useParams();

  const { setValue } = useFormContext();

  const getLiabilitiesCalculation = useGetLiabilitiesCalculation();

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.MARKETING_AND_SALES,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_MARKETING_AND_SALES.SALES_PLAN,
  );

  const [changeInOtherAssetAndLiabilitiesField] = observedFields.cashFlowLiabilities.setCalculatedValueFieldNames;

  const debouncedUpdateFunction = useMemo(
    () =>
      debounce(async (liabilitiesParams, unit) => {
        const params = {
          unit,
          businessPlanId,
          liabilitiesParams,
        };

        try {
          const { changeInOtherAssetAndLiabilities } = await getLiabilitiesCalculation.mutateAsync(params);

          setValue(changeInOtherAssetAndLiabilitiesField, changeInOtherAssetAndLiabilities);
        } catch (error) {
          if (error instanceof Error) ToastService.showError(error.message);
        }
      }, 900),
    [],
  );

  const liabilitiesCalculationHandler = useCallback(
    async (content: IFormContentTypes['cashFlowLiabilities']) => {
      if (!canAccess) return;

      const [
        prepaidExpense,
        accruedLiabilities,
        deferredRevenue,
        periodWisPrepaidExpense,
        periodWiseAccruedLiabilities,
        periodWiseDeferredRevenue,
        operationUnit,
        otherCurrentAssets,
        deferredTaxLiabilities,
        provisions,
        otherCurrentLiabilities,
        periodWiseOtherCurrentAssets,
        periodWiseDeferredTaxLiabilities,
        periodWiseProvisions,
        periodWiseOtherCurrentLiabilities,
      ] = content;

      const liabilitiesData = {
        prepaidExpense: Number(prepaidExpense),
        accruedLiabilities: Number(accruedLiabilities),
        deferredRevenue: Number(deferredRevenue),
        periodWisPrepaidExpense,
        periodWiseAccruedLiabilities,
        periodWiseDeferredRevenue,
        otherCurrentAssets,
        deferredTaxLiabilities,
        provisions,
        otherCurrentLiabilities,
        periodWiseOtherCurrentAssets,
        periodWiseDeferredTaxLiabilities,
        periodWiseProvisions,
        periodWiseOtherCurrentLiabilities,
      };

      const unit = operationUnit ?? DIMENSIONALITY_VALUES.SINGLE;

      debouncedUpdateFunction(liabilitiesData, unit);
    },
    [debouncedUpdateFunction],
  );

  return liabilitiesCalculationHandler;
};
